import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { z } from "zod";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import {
  Search,
  SlidersHorizontal,
  X,
  Gamepad2,
  Tag,
  Percent,
  Calendar,
  Sparkles,
  Rocket,
  RotateCcw,
} from "lucide-react";
import { api } from "@/lib/api";
import { GameCard } from "@/components/game-card";
import { EmptyState, ErrorState, LoadingGrid } from "@/components/empty-state";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const PLATFORMS = [
  { k: "steam", label: "Steam" },
  { k: "epic", label: "Epic Games" },
  { k: "gog", label: "GOG" },
  { k: "ea", label: "EA App" },
  { k: "ubisoft", label: "Ubisoft Connect" },
  { k: "battlenet", label: "Battle.net" },
  { k: "rockstar", label: "Rockstar" },
  { k: "microsoft", label: "Microsoft Store" },
  { k: "xbox", label: "Xbox" },
  { k: "playstation", label: "PlayStation" },
  { k: "nintendo", label: "Nintendo" },
] as const;
type PlatformKey = (typeof PLATFORMS)[number]["k"];

const DISCOUNT_STEPS = [0, 10, 25, 50, 75] as const;

const SORTS = [
  { value: "popular", label: "Popular" },
  { value: "newest", label: "Newest" },
  { value: "discount", label: "Biggest discount" },
  { value: "price_asc", label: "Lowest price" },
  { value: "price_desc", label: "Highest price" },
  { value: "title", label: "Title (A–Z)" },
  { value: "updated", label: "Recently updated" },
] as const;
type Sort = (typeof SORTS)[number]["value"];

const searchSchema = z.object({
  q: fallback(z.string(), "").default(""),
  genre: fallback(z.string(), "").default(""), // csv of slugs
  sort: fallback(
    z.enum(["popular", "newest", "discount", "price_asc", "price_desc", "title", "updated"]),
    "popular",
  ).default("popular"),
  page: fallback(z.number().int().min(1), 1).default(1),
  min: fallback(z.number().int().min(0), 0).default(0),
  max: fallback(z.number().int().min(0), 100).default(100),
  disc: fallback(z.number().int().min(0).max(100), 0).default(0),
  f2p: fallback(z.boolean(), false).default(false),
  soon: fallback(z.boolean(), false).default(false),
  ctrl: fallback(z.boolean(), false).default(false),
  plat: fallback(z.string(), "").default(""), // csv of PlatformKey
  year: fallback(z.number().int().min(0), 0).default(0),
  // Legacy deep-link params accepted from homepage:
  free: fallback(z.boolean(), false).default(false),
  release: fallback(z.string(), "").default(""),
  maxPrice: fallback(z.number().int().min(0), 0).default(0),
});

const DEFAULT_SEARCH = {
  q: "",
  genre: "",
  sort: "popular" as const,
  page: 1,
  min: 0,
  max: 100,
  disc: 0,
  f2p: false,
  soon: false,
  ctrl: false,
  plat: "",
  year: 0,
  free: false,
  release: "",
  maxPrice: 0,
};

export const Route = createFileRoute("/_site/games")({
  validateSearch: zodValidator(searchSchema),
  search: {
    middlewares: [stripSearchParams(DEFAULT_SEARCH)],
  },
  head: () => ({
    meta: [
      { title: "Browse all games — NexKey.gg" },
      { name: "description", content: "Search and compare game key prices. Filter by genre, platform, price, discount, release year and more." },
      { property: "og:title", content: "Browse all games — NexKey.gg" },
      { property: "og:description", content: "Search and compare game key prices across every shop." },
    ],
    links: [{ rel: "canonical", href: "/games" }],
  }),
  component: GamesPage,
});

function GamesPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [qLocal, setQLocal] = useState(search.q);

  // Effective filter values (merge deep-link params with UI-toggle params).
  const filterFree = search.free || search.f2p;
  const filterUpcoming = search.release === "upcoming" || search.soon;
  const filterMaxPrice =
    search.maxPrice > 0 ? search.maxPrice : search.max < 100 ? search.max : 0;
  const filterMinPrice = search.min > 0 ? search.min : 0;

  const activePlatforms = useMemo<PlatformKey[]>(
    () => (search.plat ? (search.plat.split(",").filter(Boolean) as PlatformKey[]) : []),
    [search.plat],
  );
  const activeGenres = useMemo<string[]>(
    () => (search.genre ? search.genre.split(",").filter(Boolean) : []),
    [search.genre],
  );

  const genres = useQuery({ queryKey: ["genres"], queryFn: () => api.genres() });
  const games = useQuery({
    queryKey: [
      "games",
      {
        q: search.q,
        genre: search.genre,
        sort: search.sort,
        page: search.page,
        free: filterFree,
        upcoming: filterUpcoming,
        maxPrice: filterMaxPrice,
        minPrice: filterMinPrice,
        disc: search.disc,
        year: search.year,
        ctrl: search.ctrl,
        plat: search.plat,
      },
    ],
    queryFn: () =>
      api.games({
        q: search.q || undefined,
        genre: search.genre || undefined,
        sort: search.sort,
        page: search.page,
        limit: 24,
        free: filterFree || undefined,
        upcoming: filterUpcoming || undefined,
        maxPrice: filterMaxPrice > 0 ? filterMaxPrice * 100 : undefined,
        minPrice: filterMinPrice > 0 ? filterMinPrice * 100 : undefined,
        minDiscount: search.disc > 0 ? search.disc : undefined,
        year: search.year > 0 ? search.year : undefined,
        controller: search.ctrl || undefined,
        platforms: search.plat || undefined,
      }),
  });

  const results = games.data?.data ?? [];

  const setSearch = (patch: Record<string, unknown>) =>
    navigate({ search: (s: any) => ({ ...s, ...patch }) });

  const resetAll = () => navigate({ search: () => ({ ...DEFAULT_SEARCH }) });

  const activeFilterCount =
    (activeGenres.length ? 1 : 0) +
    (search.disc > 0 ? 1 : 0) +
    (filterFree ? 1 : 0) +
    (filterUpcoming ? 1 : 0) +
    (search.ctrl ? 1 : 0) +
    (activePlatforms.length ? 1 : 0) +
    (search.year ? 1 : 0) +
    (filterMinPrice > 0 || filterMaxPrice > 0 ? 1 : 0);

  const filters = (
    <FiltersPanel
      search={search}
      activePlatforms={activePlatforms}
      activeGenres={activeGenres}
      genres={genres.data?.data ?? []}
      onChange={setSearch}
      onReset={resetAll}
    />
  );


  return (
    <div className="border-t border-border/60 bg-gradient-to-b from-background via-background to-background/60">
      {/* Hero search */}
      <section className="border-b border-border/60 bg-[radial-gradient(80%_60%_at_50%_0%,color-mix(in_oklab,var(--primary)_18%,transparent)_0%,transparent_70%)]">
        <div className="container mx-auto px-4 py-10 sm:py-14">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-surface/70 px-3 py-1 text-xs uppercase tracking-wider text-muted-foreground backdrop-blur">
              <Sparkles className="h-3.5 w-3.5 text-primary" /> Live Steam catalog
            </div>
            <h1 className="font-display text-3xl font-bold tracking-tight sm:text-5xl">
              Find the cheapest <span className="text-primary">Steam keys</span>
            </h1>
            <p className="mt-3 text-sm text-muted-foreground sm:text-base">
              Search thousands of games and compare prices instantly.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setSearch({ q: qLocal, page: 1 });
              }}
              className="group mx-auto mt-6 flex items-center gap-2 rounded-2xl border border-border bg-card/80 p-2 shadow-glow backdrop-blur transition focus-within:border-primary/60"
            >
              <Search className="ml-2 h-5 w-5 shrink-0 text-muted-foreground" />
              <input
                value={qLocal}
                onChange={(e) => setQLocal(e.target.value)}
                placeholder="Search a game, publisher, franchise…"
                className="min-w-0 flex-1 bg-transparent px-1 py-2 text-base outline-none placeholder:text-muted-foreground"
              />
              {qLocal && (
                <button
                  type="button"
                  onClick={() => {
                    setQLocal("");
                    setSearch({ q: "", page: 1 });
                  }}
                  className="rounded-md p-2 text-muted-foreground hover:bg-surface"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
              <button className="rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow transition hover:brightness-110">
                Search
              </button>
            </form>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-8">
        <div className="grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
          {/* Desktop sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">{filters}</div>
          </aside>

          {/* Results */}
          <section className="min-w-0">
            {/* Toolbar */}
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" className="lg:hidden" size="sm">
                    <SlidersHorizontal className="mr-2 h-4 w-4" />
                    Filters
                    {activeFilterCount > 0 && (
                      <span className="ml-2 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold text-primary-foreground">
                        {activeFilterCount}
                      </span>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[85vw] max-w-sm overflow-y-auto p-0">
                  <SheetHeader className="border-b border-border p-4">
                    <SheetTitle>Filters</SheetTitle>
                  </SheetHeader>
                  <div className="p-4">{filters}</div>
                </SheetContent>
              </Sheet>

              <div className="text-sm text-muted-foreground">
                {games.isSuccess && (
                  <>
                    <span className="font-semibold text-foreground">{results.length}</span>
                    {" "}of{" "}
                    <span className="font-semibold text-foreground">{games.data.total}</span> games
                  </>
                )}
              </div>

              <div className="ml-auto flex items-center gap-2">
                <label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Sort
                </label>
                <select
                  value={search.sort}
                  onChange={(e) => setSearch({ sort: e.target.value as Sort, page: 1 })}
                  className="rounded-lg border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-primary"
                >
                  {SORTS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Active chips */}
            {activeFilterCount > 0 && (
              <div className="mb-4 flex flex-wrap items-center gap-2">
                {activeGenres.map((slug) => {
                  const g = (genres.data?.data ?? []).find((x) => x.slug === slug);
                  return (
                    <Chip
                      key={slug}
                      onClear={() => {
                        const next = activeGenres.filter((s) => s !== slug).join(",");
                        setSearch({ genre: next, page: 1 });
                      }}
                    >
                      {g?.name ?? slug}
                    </Chip>
                  );
                })}
                {search.disc > 0 && (
                  <Chip onClear={() => setSearch({ disc: 0, page: 1 })}>≥ {search.disc}% off</Chip>
                )}
                {(filterMinPrice > 0 || filterMaxPrice > 0) && (
                  <Chip onClear={() => setSearch({ min: 0, max: 100, maxPrice: 0, page: 1 })}>
                    €{filterMinPrice}–€{filterMaxPrice > 0 ? filterMaxPrice : 100}
                  </Chip>
                )}
                {filterFree && (
                  <Chip onClear={() => setSearch({ f2p: false, free: false, page: 1 })}>Free to play</Chip>
                )}
                {filterUpcoming && (
                  <Chip onClear={() => setSearch({ soon: false, release: "", page: 1 })}>Coming soon</Chip>
                )}
                {search.ctrl && (
                  <Chip onClear={() => setSearch({ ctrl: false, page: 1 })}>Controller support</Chip>
                )}
                {activePlatforms.map((k) => {
                  const p = PLATFORMS.find((x) => x.k === k);
                  return (
                    <Chip
                      key={k}
                      onClear={() => {
                        const next = activePlatforms.filter((x) => x !== k).join(",");
                        setSearch({ plat: next, page: 1 });
                      }}
                    >
                      {p?.label ?? k}
                    </Chip>
                  );
                })}
                {search.year > 0 && (
                  <Chip onClear={() => setSearch({ year: 0, page: 1 })}>{search.year}</Chip>
                )}
                <button
                  onClick={resetAll}
                  className="ml-1 inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-primary"
                >
                  <RotateCcw className="h-3 w-3" /> Clear all filters
                </button>
              </div>
            )}

            {games.isLoading && <LoadingGrid count={12} />}
            {games.isError && <ErrorState message={(games.error as Error).message} />}
            {games.isSuccess && results.length === 0 && (
              <EmptyState
                title="No games match your filters."
                description="Try clearing filters or searching a different keyword."
                action={
                  <Button onClick={resetAll} variant="outline" size="sm">
                    <RotateCcw className="mr-2 h-4 w-4" /> Clear all filters
                  </Button>
                }
              />
            )}
            {games.isSuccess && results.length > 0 && (
              <>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                  {results.map((g) => (
                    <GameCard key={g.id} game={g} />
                  ))}
                </div>
                <Pagination
                  page={games.data.page}
                  total={games.data.total}
                  limit={games.data.limit}
                  onPage={(p) => setSearch({ page: p })}
                />
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}


function Chip({ children, onClear }: { children: React.ReactNode; onClear: () => void }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-1 text-xs">
      {children}
      <button
        onClick={onClear}
        className="text-muted-foreground hover:text-primary"
        aria-label="Remove filter"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

function FilterGroup({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="border-b border-border/60 py-4 first:pt-0 last:border-b-0 last:pb-0">
      <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <span className="text-primary">{icon}</span>
        {title}
      </div>
      {children}
    </div>
  );
}

function FiltersPanel({
  search,
  activePlatforms,
  activeGenres,
  genres,
  onChange,
  onReset,
}: {
  search: ReturnType<typeof Route.useSearch>;
  activePlatforms: PlatformKey[];
  activeGenres: string[];
  genres: { id: string; name: string; slug: string }[];
  onChange: (patch: Record<string, unknown>) => void;
  onReset: () => void;
}) {
  const togglePlatform = (p: PlatformKey) => {
    const set = new Set(activePlatforms);
    if (set.has(p)) set.delete(p);
    else set.add(p);
    onChange({ plat: Array.from(set).join(","), page: 1 });
  };

  const toggleGenre = (slug: string) => {
    const set = new Set(activeGenres);
    if (set.has(slug)) set.delete(slug);
    else set.add(slug);
    onChange({ genre: Array.from(set).join(","), page: 1 });
  };

  const currentYear = new Date().getFullYear();
  const years = [0, currentYear, currentYear - 1, currentYear - 2, currentYear - 5, currentYear - 10];

  const priceMax = search.maxPrice > 0 ? search.maxPrice : search.max;

  return (
    <div className="rounded-2xl border border-border bg-card/50 p-4 backdrop-blur">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="font-display text-sm font-bold uppercase tracking-wider">Filters</h2>
        <button
          onClick={onReset}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
        >
          <RotateCcw className="h-3 w-3" /> Clear all
        </button>
      </div>

      <FilterGroup title="Platform / Launcher" icon={<Gamepad2 className="h-3.5 w-3.5" />}>
        <div className="flex flex-wrap gap-2">
          {PLATFORMS.map(({ k, label }) => {
            const active = activePlatforms.includes(k);
            return (
              <button
                key={k}
                onClick={() => togglePlatform(k)}
                className={cn(
                  "rounded-lg border px-2.5 py-1.5 text-xs font-medium transition",
                  active
                    ? "border-primary bg-primary/15 text-primary"
                    : "border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground",
                )}
              >
                {label}
              </button>
            );
          })}
        </div>
      </FilterGroup>

      <FilterGroup title="Genre" icon={<Tag className="h-3.5 w-3.5" />}>
        <div className="max-h-48 space-y-1.5 overflow-y-auto pr-1">
          {genres.map((g) => (
            <label key={g.id} className="flex cursor-pointer items-center gap-2 text-sm">
              <Checkbox
                checked={activeGenres.includes(g.slug)}
                onCheckedChange={() => toggleGenre(g.slug)}
              />
              <span className="text-foreground/90">{g.name}</span>
            </label>
          ))}
        </div>
      </FilterGroup>

      <FilterGroup
        title={`Price (€${search.min} – €${priceMax}${priceMax >= 100 ? "+" : ""})`}
        icon={<Tag className="h-3.5 w-3.5" />}
      >
        <Slider
          value={[search.min, priceMax]}
          min={0}
          max={100}
          step={5}
          onValueChange={(v) =>
            onChange({
              min: v[0],
              max: v[1],
              maxPrice: v[1] < 100 ? v[1] : 0,
              page: 1,
            })
          }
          className="mt-2"
        />
        <div className="mt-2 flex items-center gap-2">
          <input
            type="number"
            min={0}
            value={search.min}
            onChange={(e) => onChange({ min: Math.max(0, Number(e.target.value) || 0), page: 1 })}
            className="w-full rounded-md border border-border bg-background px-2 py-1 text-xs"
            placeholder="Min €"
          />
          <span className="text-xs text-muted-foreground">–</span>
          <input
            type="number"
            min={0}
            value={priceMax}
            onChange={(e) => {
              const n = Math.max(0, Number(e.target.value) || 0);
              onChange({ max: n, maxPrice: n < 100 && n > 0 ? n : 0, page: 1 });
            }}
            className="w-full rounded-md border border-border bg-background px-2 py-1 text-xs"
            placeholder="Max €"
          />
        </div>
      </FilterGroup>

      <FilterGroup title="Minimum discount" icon={<Percent className="h-3.5 w-3.5" />}>
        <div className="flex flex-wrap gap-2">
          {DISCOUNT_STEPS.map((d) => {
            const active = search.disc === d;
            return (
              <button
                key={d}
                onClick={() => onChange({ disc: d, page: 1 })}
                className={cn(
                  "rounded-lg border px-3 py-1.5 text-xs font-medium transition",
                  active
                    ? "border-primary bg-primary/15 text-primary"
                    : "border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground",
                )}
              >
                {d === 0 ? "Any" : `${d}%+`}
              </button>
            );
          })}
        </div>
      </FilterGroup>

      <FilterGroup title="Release year" icon={<Calendar className="h-3.5 w-3.5" />}>
        <select
          value={search.year}
          onChange={(e) => onChange({ year: Number(e.target.value), page: 1 })}
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y === 0 ? "Any year" : `${y}`}
            </option>
          ))}
        </select>
      </FilterGroup>

      <FilterGroup title="Features" icon={<Rocket className="h-3.5 w-3.5" />}>
        <div className="space-y-2.5">
          <ToggleRow
            checked={search.free || search.f2p}
            onChange={(v) => onChange({ free: v, f2p: v, page: 1 })}
            label="Free to play"
          />
          <ToggleRow
            checked={search.release === "upcoming" || search.soon}
            onChange={(v) => onChange({ release: v ? "upcoming" : "", soon: v, page: 1 })}
            label="Coming soon"
          />
          <ToggleRow
            checked={search.ctrl}
            onChange={(v) => onChange({ ctrl: v, page: 1 })}
            label="Controller support"
          />
        </div>
      </FilterGroup>

      <div className="pt-3">
        <Button onClick={onReset} variant="outline" size="sm" className="w-full">
          <RotateCcw className="mr-2 h-3.5 w-3.5" /> Clear all filters
        </Button>
      </div>
    </div>
  );
}


function ToggleRow({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2.5 text-sm">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} />
      <span className="text-foreground/90">{label}</span>
    </label>
  );
}

function Pagination({
  page,
  total,
  limit,
  onPage,
}: {
  page: number;
  total: number;
  limit: number;
  onPage: (p: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / limit));
  if (pages <= 1) return null;
  const nums: number[] = [];
  const window = 2;
  for (let i = Math.max(1, page - window); i <= Math.min(pages, page + window); i++) nums.push(i);

  return (
    <nav className="mt-10 flex flex-wrap items-center justify-center gap-1.5">
      <PageBtn disabled={page <= 1} onClick={() => onPage(page - 1)}>
        Previous
      </PageBtn>
      {nums[0] > 1 && (
        <>
          <PageNum active={page === 1} onClick={() => onPage(1)}>
            1
          </PageNum>
          {nums[0] > 2 && <span className="px-1 text-muted-foreground">…</span>}
        </>
      )}
      {nums.map((n) => (
        <PageNum key={n} active={n === page} onClick={() => onPage(n)}>
          {n}
        </PageNum>
      ))}
      {nums[nums.length - 1] < pages && (
        <>
          {nums[nums.length - 1] < pages - 1 && <span className="px-1 text-muted-foreground">…</span>}
          <PageNum active={page === pages} onClick={() => onPage(pages)}>
            {pages}
          </PageNum>
        </>
      )}
      <PageBtn disabled={page >= pages} onClick={() => onPage(page + 1)}>
        Next
      </PageBtn>
    </nav>
  );
}

function PageBtn({
  children,
  disabled,
  onClick,
}: {
  children: React.ReactNode;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm transition hover:border-primary/60 hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-border disabled:hover:text-foreground"
    >
      {children}
    </button>
  );
}

function PageNum({
  children,
  active,
  onClick,
}: {
  children: React.ReactNode;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "min-w-9 rounded-lg border px-3 py-1.5 text-sm font-medium transition",
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-surface text-foreground hover:border-primary/60 hover:text-primary",
      )}
    >
      {children}
    </button>
  );
}
